import Router from 'next/router'

import { SHWError } from '@/common/error'

import { trackEvent } from '@/client/services/analytics'

import { disableLoadingState, enableLoadingState } from './utils/loadingState'
import refreshPage from './utils/refreshPage'

type RPCRequestConfig = {
  disableProgressBar?: boolean
  disableTracking?: boolean
  analyticsMetadata?: Record<string, unknown>
}

function makeClient<T>(
  client: (path: string[], arg: unknown, config?: RPCRequestConfig) => Promise<T>
  // biome-ignore lint/suspicious/noExplicitAny: We infer the types separately, below
): any {
  function makeLayer(path: string[]): unknown {
    return new Proxy(
      (arg: unknown, config?: RPCRequestConfig) =>
        client(path, arg, config).then(
          result => {
            if (config?.disableTracking !== true) {
              trackEvent(
                {
                  category: 'RPC Call Success',
                  action: `${path}`,
                },
                config?.analyticsMetadata
              )
            }

            return result
          },
          err => {
            if (config?.disableTracking !== true) {
              trackEvent(
                {
                  category: 'RPC Call Failure',
                  action: `${path}`,
                },
                config?.analyticsMetadata
              )
            }
            throw err
          }
        ),
      {
        get(_obj, name: string) {
          return makeLayer(path.concat([name]))
        },
      }
    )
  }
  return makeLayer([])
}

async function rpcRequest(
  path: string[],
  arg: unknown,
  config: RPCRequestConfig = { disableProgressBar: false }
) {
  try {
    if (config.disableProgressBar === false) {
      enableLoadingState()
    }
    const res = await fetch('/api/rpc', {
      body: JSON.stringify({
        path,
        args: [arg],
      }),
      method: 'POST',
      credentials: 'include',
    })
    const text = await res.text()

    let parsed: Record<string, unknown>

    try {
      parsed = JSON.parse(text)
    } catch (_) {
      throw new SHWError({ externalMessage: text })
    }

    if (
      parsed != null &&
      typeof parsed === 'object' &&
      'type' in parsed &&
      parsed.type === 'error' &&
      'message' in parsed
    ) {
      if ('externalMessage' in parsed) {
        throw new SHWError({
          // @ts-expect-error: Its coming from our backend, and passed type-checking there
          externalMessage: parsed.externalMessage,
        })
      }

      throw new SHWError({
        externalMessage: `${parsed.message}`,
      })
    }
    return parsed
  } finally {
    if (config.disableProgressBar === false) {
      disableLoadingState()
    }
  }
}

// oxlint-disable-next-line consistent-type-imports
type RPCServer = typeof import('../server/api/methods')
// We override the RPCOrig typings to get rid of the "this"
// typing they have on the server side
type RPCClient = {
  [P in keyof RPCServer]: (
    arg: Parameters<RPCServer[P]>[1],
    config?: RPCRequestConfig
  ) => ReturnType<RPCServer[P]>
}

export const RPCRefresh: RPCClient = makeClient(async (path, args, config) => {
  const result = await rpcRequest(path, args, config)
  await refreshPage()
  return result
})

/** RPCReload Usage Warning:
 * In the majority of cases, when you want an RPC call to trigger updating components (e.g.
 * based on updated data after the RPC call), you can use RPCRefresh which allows React to
 * optimize the UI update. However, because RPCRefresh doesn't re-render components completely,
 * state is maintained in components when sometimes you may want to re-initialie stateful
 * components completely after the RPC call. In these cases, you can use RPCReload to trigger
 * an entire page reload which will completely re-render everything on the page.
 *
 * Note that there are smarter/more optimal ways to properly trigger the component updates
 * that also provide a better UX (since they don't reload the entire page) but this method
 * is just a lot more convenient. Use it sparingly!
 *
 * See this thread for more context:
 * https://schoolhouseproduct.slack.com/archives/C021RRZFA9H/p1641277358016500
 * @deprecated: Depending on your usecase, you may want to use RPCRefresh instead
 */
export const RPCReload: RPCClient = makeClient(async (path, args, config) => {
  const result = await rpcRequest(path, args, config)
  Router.reload()
  return result
})

const RPC: RPCClient = makeClient(rpcRequest)

export default RPC
